import * as React from 'react';
import {Field, Validator, WrappedFieldProps} from 'redux-form';
import {FormControl, FormHelperText} from '@material-ui/core';
import Select, {Props as SelectProps} from 'react-select';
import Creatable from 'react-select/creatable';
import {useTranslation} from 'react-i18next';
import {CustomStyledControl} from './ReactSelectComponents';
import {injectRequiredValidation} from '../../functions/injectRequiredValidation';

interface FieldProps extends SelectProps {
    label?: string;
    required?: boolean;
    name: string;
    choices: Array<{ [key in string]: any }>;
    optionValue?: string | Function;
    optionName?: string | Function;
    disableChoicesTranslate?: boolean;
    fullWidth?: boolean;
    validate?: Validator | Array<Validator>;
    isCreatable?: boolean;
    helperText?: string;
}

type ExternalFieldProps = FieldProps & SelectProps;

type FormatReturningValue = { value: any, label: string } | Array<{ value: any, label: string }> | undefined;

interface AutocompleteInputProps extends SelectProps, WrappedFieldProps {
    label: string;
    required?: boolean;
    fullWidth?: boolean;
    isCreatable?: boolean;
    helperText?: string;
}

const AutocompleteInputComponent: React.FunctionComponent<AutocompleteInputProps> = ({
    input,
    meta: {touched, error, invalid},
    fullWidth,
    required,
    options,
    isCreatable,
    helperText,
    className,
    ...rest
}: AutocompleteInputProps) => {
    const Component = isCreatable ? Creatable : Select;

    return (
        <FormControl fullWidth={fullWidth} required={required} error={touched && invalid} className={className}>
            <Component
                menuPlacement='auto'
                {...rest}
                // @ts-ignore
                options={options}
                onChange={input.onChange}
                value={input.value}
                components={{Control: CustomStyledControl}}
            />
            <FormHelperText>{(touched && error) || helperText}</FormHelperText>
        </FormControl>

    );
};

const ReduxAutocompleteFieldController: React.FunctionComponent<FieldProps> = ({
    name,
    label,
    required,
    choices = [],
    disableChoicesTranslate,
    optionName,
    optionValue,
    validate,
    ...rest
}: FieldProps) => {
    const {t: translate} = useTranslation();

    const options = React.useMemo(() =>
        choices.map((choice) => {
            let {id, name} = choice;
            if (optionValue) {
                if (typeof optionValue === 'function') {
                    id = optionValue(choice);
                } else {
                    id = choice[optionValue];
                }
            }

            if (optionName) {
                if (typeof optionName === 'function') {
                    name = optionName(choice);
                } else {
                    name = choice[optionName];
                }
            }

            return ({
                value: id,
                label: disableChoicesTranslate ? name : translate(name),
            });
        }), [choices]);

    const resultValidation = React.useMemo(
        injectRequiredValidation(!!required, validate),
        [required, validate],
    );

    return (
        <Field
            name={name}
            // @ts-ignore
            label={label ? translate(label) : name}
            component={AutocompleteInputComponent}
            validate={resultValidation}
            required={required}
            options={options}
            parse={(input: any): any => {
                if (rest.isMulti) {
                    return input && Array.isArray(input) ? input.map((el: any) => el?.value) : [];
                }

                return input?.value;
            }}
            format={(inputValue: any): FormatReturningValue => {
                if (rest.isMulti) {
                    return inputValue && Array.isArray(inputValue) ?
                        options.filter(({value}) => inputValue.includes(value)) :
                        [];
                }

                return options.find(({value}) => value === inputValue);
            }}
            {...rest}
        />
    );
};

const ReduxAutocompleteField: React.FunctionComponent<ExternalFieldProps> = ReduxAutocompleteFieldController;

export default ReduxAutocompleteField;